import { SensorData } from '../../../../../types/systems';
import { FieldRules, rules, runRules, ValidatedJsonType, ValidationResult } from '../validator';

export function isSensorJson(value: ValidatedJsonType): ValidationResult {
	const json = value as SensorData;

	const fields: FieldRules[] = [
		{ key: 'name', actual: json.name, rules: [rules.isString(json.name)] },
		{ key: 'side', actual: json.side, rules: [rules.isNumber(json.side)] },
		{
			key: 'sector_angle',
			actual: json.sector_angle,
			rules: [rules.isNumber(json.sector_angle)],
		},
		{ key: 'rotates', actual: json.rotates, rules: [rules.isBoolean(json.rotates)] },
		{ key: 'perchable', actual: json.perchable, rules: [rules.isBoolean(json.perchable)] },
		{
			key: 'penetrates_canopy',
			actual: json.penetrates_canopy,
			rules: [rules.isBoolean(json.penetrates_canopy)],
		},
		{ key: 'emitter', actual: json.emitter, rules: [rules.isBoolean(json.emitter)] },
		{ key: 'id', actual: json.id, rules: [rules.optional(json.id, rules.isNumber(json.id))] },
		{
			key: 'confounders',
			actual: json.confounders,
			rules: [
				rules.isArray(json.confounders),
				rules.everyItem(
					json.confounders,
					'every confounder must be a non-null object',
					(c: any) => typeof c === 'object' && c !== null
				),
			],
		},
		{
			key: 'sensing_performance',
			actual: json.sensing_performance,
			rules: [
				rules.isArray(json.sensing_performance),
				rules.everyItem(
					json.sensing_performance,
					'every item must have numeric target, detect_range, track_range, classify_range',
					(p: any) =>
						typeof p.target === 'number' &&
						typeof p.detect_range === 'number' &&
						typeof p.track_range === 'number' &&
						typeof p.classify_range === 'number'
				),
			],
		},
		{
			key: 'rcs_probability',
			actual: json.rcs_probability,
			rules: [
				rules.isArray(json.rcs_probability),
				rules.everyItem(
					json.rcs_probability,
					'every item must have numeric range and probability',
					(r: any) => typeof r.range === 'number' && typeof r.probability === 'number'
				),
			],
		},
		{
			key: 'detection_time',
			actual: json.detection_time,
			rules: [rules.optional(json.detection_time, rules.isNumber(json.detection_time))],
		},
		{
			key: 'tracking_time',
			actual: json.tracking_time,
			rules: [rules.optional(json.tracking_time, rules.isNumber(json.tracking_time))],
		},
		{
			key: 'cued_tracking_time',
			actual: json.cued_tracking_time,
			rules: [
				rules.optional(json.cued_tracking_time, rules.isNumber(json.cued_tracking_time)),
			],
		},
		{ key: 'has_cueing', actual: json.has_cueing, rules: [rules.isBoolean(json.has_cueing)] },
		{
			key: 'min_rcs',
			actual: json.min_rcs,
			rules: [rules.optional(json.min_rcs, rules.isNumber(json.min_rcs))],
		},
		{
			key: 'cost',
			actual: json.cost,
			rules: [
				rules.custom(
					'must be a number or a string without spaces',
					() =>
						typeof json.cost === 'number' ||
						(typeof json.cost === 'string' && !json.cost.includes(' '))
				),
			],
		},
	];

	return runRules(fields);
}

export const validateSensor = (data: ValidatedJsonType): ValidationResult => {
	return isSensorJson(data);
};
